import { useState } from "react";
import { Link } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { specials } from "@/mocks/specials";
import { getMovieById } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Special() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = specials[activeIndex];

  usePageMeta({
    title: `${active.title} - 日本伦理专题合集`,
    description: `${active.title}专题：${active.subtitle}。${active.intro.slice(0, 68)}`,
    keywords: `${active.title},日本伦理专题,伦理影视合集,${active.subtitle}`,
  });

  return (
    <>
      <section className="border-b border-background-800 bg-background-900">
        <div className="mx-auto max-w-[1440px] px-4 pb-8 pt-24 md:px-12 md:pb-10 md:pt-32">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">专题合集</span>
          </nav>
          <h1 className="mt-5 text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
            专题合集
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            围绕一个议题集中编排作品与解析，提供比单篇影评更完整的阅读路径。当前收录 3 个专题，共涉及 12 部作品。
          </p>

          <div className="mt-7 flex gap-2 overflow-x-auto pb-1">
            {specials.map((special, index) => (
              <button
                key={special.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`flex shrink-0 items-center gap-3 rounded-md border px-4 py-2.5 text-sm transition-colors ${
                  index === activeIndex
                    ? "border-accent-500 bg-accent-500 text-background-950"
                    : "border-background-700 text-foreground-600 hover:border-accent-500 hover:text-accent-400"
                }`}
              >
                <span
                  className={`font-label text-xs ${
                    index === activeIndex ? "text-background-950/70" : "text-foreground-500"
                  }`}
                >
                  0{index + 1}
                </span>
                <span className="whitespace-nowrap">{special.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-10 md:px-12 md:py-14">
        <div className="grid grid-cols-1 gap-0 overflow-hidden rounded-lg border border-background-800 lg:grid-cols-2">
          <div className="relative min-h-[240px] lg:min-h-[340px]">
            <img
              src={active.image}
              alt={`${active.title}专题封面`}
              title={`${active.title} - 日本伦理专题合集`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-background-950/70"></div>
          </div>
          <div className="bg-background-900 px-5 py-8 md:px-10 md:py-10">
            <p className="font-label text-xs uppercase tracking-[0.24em] text-accent-500">
              Special 0{activeIndex + 1}
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-foreground-950 md:text-3xl">
              {active.title}
            </h2>
            <p className="mt-1 text-sm text-foreground-500">{active.subtitle}</p>
            <p className="mt-5 text-sm leading-7 text-foreground-600">{active.intro}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {active.groups.map((group) => (
                <span
                  key={group.title}
                  className="rounded-full bg-secondary-800/50 px-3 py-1 text-xs text-secondary-100"
                >
                  {group.title}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 space-y-14">
          {active.groups.map((group, groupIndex) => {
            const groupMovies = group.movieIds
              .map((movieId) => getMovieById(movieId))
              .filter((movie): movie is NonNullable<typeof movie> => Boolean(movie));

            return (
              <div key={group.title}>
                <div className="flex flex-col gap-3 border-b border-background-800 pb-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="font-label text-xs uppercase tracking-[0.22em] text-foreground-500">
                      章节 0{groupIndex + 1}
                    </p>
                    <h2 className="mt-2 text-xl font-semibold text-foreground-950 md:text-2xl">
                      {group.title}
                    </h2>
                  </div>
                  <span className="text-xs text-foreground-500">{groupMovies.length} 部作品</span>
                </div>
                <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-600">{group.note}</p>
                <div className="mt-7 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-5">
                  {groupMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} showSummary={false} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col gap-4 rounded-lg border border-background-800 bg-background-900 px-5 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-label text-xs uppercase tracking-[0.22em] text-foreground-500">
              专题导航
            </p>
            <p className="mt-2 text-sm text-foreground-700">
              下一个专题：{specials[(activeIndex + 1) % specials.length].title}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setActiveIndex((index) => (index + 1) % specials.length)}
            className="flex w-fit items-center gap-2 rounded-md border border-accent-500 px-5 py-2.5 text-sm text-accent-400 transition-colors hover:bg-accent-500 hover:text-background-950"
          >
            切换专题
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </button>
        </div>
      </section>
    </>
  );
}